import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Typography } from '@memoresa/ui-components';
import clsx from 'clsx';
import { debounce } from 'debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { byString } from '../../utils/functions/reactHookFormErrorParser';
import useFocusedElement from '../../utils/hooks/useFocusedElement';

type InputProps = JSX.IntrinsicElements['input'];
interface ITextInput extends InputProps {
  label?: string;
  register: UseFormRegisterReturn;
  showErrorMessages?: boolean;
  important?: boolean;
  onEnterAction?: () => void;
}

export const TextInput: React.FC<ITextInput> = props => {
  const methods = useFormContext();
  const { t } = useTranslation();
  const showErrorMessages = props.showErrorMessages ?? true;
  const [isFocused, setIsFocused] = useState(false);
  const focusedElement = useFocusedElement();
  useEffect(() => {
    setIsFocused(
      focusedElement.name === props.register.name ||
        focusedElement.id === `${props.register.name}-button`
    );
  }, [focusedElement, props.register.name]);
  //This is necessary to remove all non-native props.
  const nativeProps = (({
    showErrorMessages,
    important,
    onEnterAction,
    ...rest
  }) => rest)(props);
  const debouncedFunction = useCallback(
    debounce(props.register.onChange, 100),
    []
  );
  const handleChange = e => debouncedFunction(e);
  return (
    <div className="flex flex-col w-full space-y-1">
      {props.label && (
        <label
          htmlFor={props.id}
          className="text-[16px] font-medium text-primary"
        >
          {props.label}:
          {props.required && <span className="ml-1 text-error">*</span>}
        </label>
      )}
      <div className="relative w-full">
        <input
          {...nativeProps}
          {...props.register}
          size={1}
          className={clsx(
            'px-2 w-full rounded-md border border-1 focus:outline-none focus:border-2  font-medium h-12 ',
            byString(methods.formState.errors, props.register.name)
              ? 'border-error focus:border-error'
              : 'border-primary-opacity-30 focus:border-primary',
            props.disabled
              ? 'text-primary-45 bg-primary-opacity-5 !cursor-not-allowed'
              : 'text-primary',
            props.important ? 'text-[20px] uppercase' : 'text-[15px]'
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={e => {
            if (e.relatedTarget?.id !== `${props.register.name}-button`) {
              setIsFocused(false);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              props.onEnterAction?.();
            }
            props.onKeyDown?.(e);
          }}
          onChange={handleChange}
        />
        {isFocused && methods.getValues(props.register.name) && (
          <button
            id={`${props.register.name}-button`}
            onClick={e => {
              methods.setValue(props.register.name, '', {
                shouldValidate: true,
              });
              (
                document.querySelector(
                  `[name='${props.register.name}']`
                ) as HTMLInputElement
              ).focus();
              methods.trigger();
            }}
            className="absolute top-0 sm:translate-y-1/2 translate-y-1/4 right-2"
            tabIndex={-1}
          >
            <Icon
              path={mdiClose}
              size={1}
              className={clsx(
                'text-primary-20 rounded-full cursor-pointer hover:bg-gray-100'
              )}
            />
          </button>
        )}
      </div>

      {showErrorMessages &&
        byString(methods.formState.errors, props.register.name) && (
          <Typography color="text-error text-opacity-70" variant="caption">
            {byString(methods.formState.errors, props.register.name)?.message ||
              (byString(methods.formState.errors, props.register.name)?.type ===
                'required' &&
                t('general.field.required'))}
          </Typography>
        )}
    </div>
  );
};
